import React from "react";
import { Download, ArrowUpRight, Sparkles, MapPin } from "lucide-react";
import { PROFILE } from "../../data/profile";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-36 pb-24 sm:pt-44 sm:pb-32"
    >
      {/* grid bg */}
      <div className="pointer-events-none absolute inset-0 grid-overlay opacity-60" />
      {/* glow */}
      <div className="pointer-events-none absolute -top-20 right-[-10%] h-[520px] w-[520px] rounded-full bg-[var(--jewel)] opacity-[0.07] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 left-[-10%] h-[420px] w-[420px] rounded-full bg-[var(--jewel-2)] opacity-[0.08] blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-5">
        {/* top meta row */}
        <div className="reveal flex flex-wrap items-center justify-between gap-3 mb-10">
          <span className="text-eyebrow flex items-center gap-2" data-testid="hero-eyebrow">
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--jewel)] dot-live" />
            Available · Open to roles & freelance
          </span>
          <span className="text-eyebrow flex items-center gap-1.5">
            <MapPin size={12} /> {PROFILE.location}
          </span>
        </div>

        {/* huge editorial heading */}
        <h1
          data-testid="hero-heading"
          className="text-display text-foreground reveal reveal-d1"
          style={{ fontSize: "clamp(3.2rem, 11vw, 12rem)" }}
        >
          <span className="block">Shivmurat</span>
          <span className="block">
            <em className="italic font-serif text-[var(--jewel)]">Gupta</em>
            <span className="blink ml-3 inline-block h-[0.8em] w-[0.08em] translate-y-[0.05em] bg-[var(--jewel)]" />
          </span>
        </h1>

        {/* tagline grid */}
        <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-12 md:items-end">
          <div className="md:col-span-7 reveal reveal-d2">
            <p className="font-mono-ed text-xs uppercase tracking-[0.22em] text-muted-foreground mb-4">
              {PROFILE.titles.join("  ·  ")}
            </p>
            <p
              data-testid="hero-tagline"
              className="font-serif text-2xl sm:text-3xl md:text-[2.1rem] leading-snug text-foreground/85"
            >
              Software Developer with{" "}
              <span className="text-[var(--jewel)] font-semibold">{PROFILE.years_experience} years</span>{" "}
              shipping <em className="italic">scalable web apps</em>,{" "}
              <em className="italic">AI-powered systems</em> and high-performance backends with{" "}
              <span className="font-mono-ed text-base align-middle px-1.5 py-0.5 rounded bg-white/5 border border-white/10">
                Python · Django · AWS · OpenAI
              </span>
              .
            </p>
          </div>

          <div className="md:col-span-5 reveal reveal-d3">
            <div className="glass rounded-2xl p-5">
              <p className="text-eyebrow mb-3">// Quick stats</p>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { v: "4+", l: "Years" },
                  { v: "9", l: "Projects" },
                  { v: "30+", l: "Tools" },
                ].map((s) => (
                  <div key={s.l}>
                    <div className="font-serif text-4xl text-foreground">{s.v}</div>
                    <div className="font-mono-ed text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                      {s.l}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className="mt-10 flex flex-wrap items-center gap-3 reveal reveal-d4">
          <a
            href={PROFILE.resume_url}
            download="Shivmurat_Gupta_Resume.pdf"
            target="_blank"
            rel="noreferrer"
            data-testid="hero-cta-resume"
            className="inline-flex items-center gap-2 btn-ink px-5 py-3 text-sm"
          >
            <Download size={16} /> Download Resume
          </a>
          <a
            href={`mailto:${PROFILE.email}?subject=Let's%20work%20together`}
            data-testid="hero-cta-hire"
            className="inline-flex items-center gap-2 btn-ghost px-5 py-3 text-sm"
          >
            Hire me <ArrowUpRight size={16} />
          </a>
          <a
            href="#ai"
            data-testid="hero-cta-ai"
            className="inline-flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Sparkles size={14} /> Or chat with my AI twin
          </a>
        </div>
      </div>

      {/* marquee strip */}
      <div className="mt-20 relative overflow-hidden border-y border-white/[0.06] py-6">
        <div className="marquee gap-12 pr-12 font-serif text-3xl sm:text-4xl text-foreground/30 italic">
          {Array.from({ length: 2 }).map((_, i) => (
            <React.Fragment key={i}>
              {[
                "Python",
                "·",
                "Django",
                "·",
                "FastAPI",
                "·",
                "AWS",
                "·",
                "OpenAI",
                "·",
                "YOLO",
                "·",
                "MediaPipe",
                "·",
                "Rust",
                "·",
                "WASM",
                "·",
                "Redis",
                "·",
                "PostgreSQL",
              ].map((t, idx) => (
                <span key={`${i}-${idx}`} className="whitespace-nowrap">
                  {t}
                </span>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}
